import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, ArrowUpRight, ShieldCheck, Camera, MapPin, HardHat } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal } from "@/components/site/Reveal";
import { CtaButtons, Faq, Etapes } from "@/components/site/Seo";
import { FormuleCard } from "@/pages/public/Formules";
import { AppQr } from "@/components/site/AppQr";
import { COMPANY } from "@/data/site";
import { SERVICES, FAQ_GENERALE, ETAPES } from "@/data/services";
import { api } from "@/lib/api";
import usePageMeta from "@/hooks/usePageMeta";

const STATS = [
  { v: "Brest", l: "et ses environs, Finistère" },
  { v: "24h/24", l: "joignable 7j/7" },
  { v: "Photos", l: "compte rendu de chaque visite" },
];

const Hero = () => (
  <section className="relative min-h-[88vh] overflow-hidden bg-[#0F2840] grain" data-testid="home-hero">
    <img src="/images/hero.jpg" alt="Littoral breton près de Brest dans le Finistère" width="1600" height="900" fetchpriority="high" className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0F2840] via-[#0F2840]/75 to-[#0F2840]/10" />
    <div className="absolute right-[8%] top-1/2 hidden -translate-y-1/2 lg:block">
      <motion.img src="/logo.png" alt="Logo Conciergerie Familly 29" data-testid="hero-logo" width="400" height="400"
        initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.2 }}
        className="w-[340px] rounded-full bg-white shadow-2xl xl:w-[400px]" />
    </div>
    <div className="relative mx-auto flex min-h-[88vh] max-w-7xl flex-col justify-end px-5 pb-16 pt-28 lg:px-8">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
        <p className="eyebrow !text-[#ff8080]">Conciergerie à Brest · Finistère</p>
        <h1 className="mt-5 max-w-3xl font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="home-h1">
          Conciergerie à Brest : votre logement <em className="italic text-[#ff8080]">entre de bonnes mains</em>
        </h1>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-white/85 md:text-lg">
          Visites de surveillance, remise de clés, entretien et petits travaux à Brest et dans les environs.
        </p>
        <div className="mt-10"><CtaButtons id="hero" light /></div>
      </motion.div>
      <div className="mt-16 grid max-w-2xl grid-cols-3 gap-6 border-t border-white/20 pt-8">
        {STATS.map((s, i) => (
          <motion.div key={s.l} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 + i * 0.12 }} data-testid={`hero-stat-${i}`}>
            <p className="font-display text-3xl font-semibold text-white md:text-4xl">{s.v}</p>
            <p className="mt-1 text-xs text-white/70 md:text-sm">{s.l}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const Intro = () => (
  <section className="mx-auto grid max-w-7xl gap-10 px-5 py-20 md:grid-cols-12 lg:px-8" data-testid="home-intro">
    <Reveal className="md:col-span-7">
      <h2 className="font-display text-3xl font-semibold tracking-tight md:text-4xl">Partir l'esprit tranquille</h2>
      <p className="mt-5 text-lg leading-relaxed text-slate-700">
        Vous partez en déplacement, en mission ou en vacances ? Conciergerie Familly 29 vous accompagne dans l'entretien et la surveillance ponctuelle de votre logement. Nous réalisons des visites programmées, vérifions les anomalies visibles, aérons votre habitation et pouvons coordonner la venue d'artisans selon vos besoins.
      </p>
      <p className="mt-4 leading-relaxed text-slate-700">Grâce à notre expérience dans la maintenance du bâtiment, nous associons proximité, suivi personnalisé et regard technique.</p>
      <p className="mt-4 font-semibold text-[#0F2840]">Un interlocuteur local, un compte rendu de vos visites et des prestations adaptées à votre situation.</p>
    </Reveal>
    <Reveal className="md:col-span-5" delay={0.1}>
      <div className="rounded-lg bg-[#0F2840] p-8 text-white">
        <HardHat className="h-8 w-8 text-[#ff8080]" />
        <h3 className="mt-4 font-display text-2xl">Le regard d'un professionnel du bâtiment</h3>
        <p className="mt-3 text-sm leading-relaxed text-white/80">Fuite naissante, humidité, menuiserie qui ferme mal : nous savons repérer ce qui mérite votre attention et vous l'expliquer simplement.</p>
        <p className="mt-4 text-sm text-white/70">{COMPANY.horaires}.</p>
      </div>
    </Reveal>
  </section>
);

const PrestationsBento = () => (
  <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8" data-testid="home-prestations">
    <div className="grid gap-6 md:grid-cols-12 md:items-end">
      <Reveal className="md:col-span-7">
        <p className="eyebrow">Nos prestations</p>
        <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight md:text-4xl">Nos services de conciergerie à Brest</h2>
      </Reveal>
      <Reveal className="md:col-span-5 md:text-right" delay={0.1}>
        <Link to="/prestations" className="inline-flex items-center gap-1 text-sm font-bold text-[#D82C2C] hover:underline" data-testid="home-all-prestations-link">
          Toutes nos prestations <ArrowUpRight className="h-4 w-4" />
        </Link>
      </Reveal>
    </div>
    <div className="mt-12 grid auto-rows-[220px] grid-cols-1 gap-5 md:grid-cols-12">
      {SERVICES.map((p, i) => {
        const span = ["md:col-span-7 md:row-span-2", "md:col-span-5", "md:col-span-5", "md:col-span-4", "md:col-span-8"][i];
        return (
          <Reveal key={p.slug} delay={i * 0.06} className={span}>
            <Link to={p.path} data-testid={`home-prestation-${p.slug}`} className="group relative block h-full overflow-hidden rounded-lg bg-[#0F2840]">
              <img src={p.image} alt={p.alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F2840] via-[#0F2840]/40 to-transparent" />
              <div className="relative flex h-full flex-col justify-end p-6 text-white">
                <p.icon className="h-6 w-6 text-[#ff8080]" />
                <h3 className="mt-3 font-display text-2xl font-semibold">{p.nom}</h3>
                <p className="mt-1 max-w-sm text-sm text-white/80">{p.inclus.slice(0, 3).join(" · ")}</p>
              </div>
            </Link>
          </Reveal>
        );
      })}
    </div>
  </section>
);

const Trust = () => (
  <section className="bg-slate-50 py-24" data-testid="home-trust">
    <div className="mx-auto grid max-w-7xl gap-12 px-5 md:grid-cols-12 lg:px-8">
      <Reveal className="md:col-span-5">
        <p className="eyebrow">Pourquoi nous faire confiance</p>
        <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight md:text-4xl">Une équipe locale, une gestion soignée, un service humain.</h2>
        <img src="/images/port.jpg" alt="Port de Brest, secteur d'intervention de la conciergerie" loading="lazy" className="mt-8 h-64 w-full rounded-lg object-cover" />
      </Reveal>
      <div className="grid gap-5 sm:grid-cols-2 md:col-span-7">
        {[
          { i: MapPin, t: "Proximité", d: "Un interlocuteur local, à Brest et dans les environs." },
          { i: ShieldCheck, t: "Discrétion et confiance", d: "Vos clés et vos accès gérés selon des règles écrites et tracées." },
          { i: Camera, t: "Comptes rendus photo", d: "Après chaque passage, un compte rendu disponible dans votre espace client." },
          { i: HardHat, t: "Regard technique", d: "Une expérience de la maintenance du bâtiment au service de votre logement." },
        ].map((x, k) => (
          <Reveal key={x.t} delay={k * 0.08}>
            <div className="h-full rounded-lg border border-[#0F2840]/10 bg-white p-7 transition-transform duration-300 hover:-translate-y-1">
              <x.i className="h-7 w-7 text-[#D82C2C]" />
              <h3 className="mt-5 text-lg font-bold">{x.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{x.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const FormulesPreview = () => {
  const { data = [] } = useQuery({ queryKey: ["formules"], queryFn: () => api.get("/public/formules").then((r) => r.data) });
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8" data-testid="home-formules">
      <Reveal>
        <p className="eyebrow">Formules & abonnements</p>
        <h2 className="mt-3 max-w-2xl font-display text-3xl font-semibold tracking-tight md:text-4xl">Des formules claires, adaptées à votre situation.</h2>
        <Link to="/tarifs-conciergerie-brest/" className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-[#D82C2C] hover:underline" data-testid="home-tarifs-link">Tous nos tarifs <ArrowUpRight className="h-4 w-4" /></Link>
      </Reveal>
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {data.map((f, i) => <Reveal key={f.id} delay={i * 0.08}><FormuleCard f={f} /></Reveal>)}
      </div>
    </section>
  );
};

const AppSection = () => (
  <section className="bg-[#0F2840] py-20 text-white" data-testid="home-app-section">
    <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 md:grid-cols-12 lg:px-8">
      <Reveal className="md:col-span-8">
        <p className="eyebrow !text-[#ff8080]">Votre application</p>
        <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight md:text-4xl">Suivez l'avancement de vos interventions et de vos travaux, en direct.</h2>
        <p className="mt-4 max-w-xl text-white/75">Rendez-vous, comptes rendus photo, avancement des travaux, devis et factures : tout est réuni dans votre espace sécurisé, sur ordinateur et smartphone.</p>
        <a href="/connexion" data-testid="home-app-link" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#D82C2C] px-7 py-4 text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5">
          Accéder à mon espace <ArrowRight className="h-4 w-4" />
        </a>
        <p className="mt-4 text-xs text-white/60">Sur iPhone : Partager → « Sur l'écran d'accueil ». Sur Android : menu → « Installer l'application ».</p>
      </Reveal>
      <Reveal className="md:col-span-4 md:text-center" delay={0.1}>
        <AppQr size={170} id="home-qr" />
        <p className="mt-3 text-sm text-white/75">Scannez pour ouvrir l'application</p>
      </Reveal>
    </div>
  </section>
);

const CtaBand = () => (
  <section className="relative overflow-hidden bg-[#D82C2C]" data-testid="home-cta">
    <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-5 py-16 md:flex-row md:items-center lg:px-8">
      <h2 className="max-w-2xl font-display text-3xl font-semibold text-white md:text-4xl">Un déplacement, une mission, un logement à confier ?</h2>
      <div className="flex flex-wrap gap-3">
        <Link to="/devis" data-testid="cta-devis-btn" className="rounded-full bg-white px-8 py-4 text-sm font-bold text-[#0F2840] transition-transform duration-200 hover:-translate-y-0.5">Demander un devis</Link>
        <a href={COMPANY.telephoneHref} data-testid="cta-call-btn" className="rounded-full border border-white px-8 py-4 text-sm font-bold text-white hover:bg-white hover:text-[#0F2840]">Appeler</a>
      </div>
    </div>
  </section>
);

const Process = () => (
  <section className="bg-slate-50 py-20" data-testid="home-process">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <h2 className="font-display text-3xl font-semibold tracking-tight md:text-4xl">Comment se déroule une intervention ?</h2>
      <div className="mt-10"><Etapes items={ETAPES} /></div>
    </div>
  </section>
);

const FaqSection = () => (
  <section className="mx-auto max-w-4xl px-5 py-20 lg:px-8" data-testid="home-faq">
    <h2 className="font-display text-3xl font-semibold tracking-tight md:text-4xl">Questions fréquentes</h2>
    <div className="mt-6"><Faq items={FAQ_GENERALE} id="home" /></div>
  </section>
);

export default function Home() {
  usePageMeta(null, "Conciergerie à Brest : visites de surveillance, remise de clés, entretien et petits travaux pour votre logement pendant vos absences. Devis gratuit, compte rendu après chaque visite.");
  return (
    <>
      <Hero />
      <Intro />
      <PrestationsBento />
      <Trust />
      <Process />
      <FormulesPreview />
      <FaqSection />
      <AppSection />
      <CtaBand />
    </>
  );
}
